import Mark from 'mark.js';
import { useCallback, useEffect, useState } from 'react';
import { Arrow } from '../SVGs';

const Search = ({}: object) => {
  const [search, setSearch] = useState<string>('');
  const [matches, setMatches] = useState<HTMLElement[] | null>(null);
  const [matchInFocus, setMatchInFocus] = useState<number>(0);

  useEffect(() => {
    const markInstance = new Mark('div.searchable');
    markInstance.unmark({
      done: () => {
        markInstance.mark(search);
        const newMatches = Array.from(document.getElementsByTagName('mark'));
        setMatches(newMatches);
        const firstMatch = newMatches.length > 0 ? 0 : -1;
        setMatchInFocus(firstMatch);
        if (firstMatch !== -1) {
          highlightMatch(newMatches[firstMatch]);
          newMatches[0].scrollIntoView({ block: 'center' });
        }
      },
    });
  }, [search]);

  useEffect(() => {
    if (matchInFocus !== -1 && matches && matches.length > 0) {
      matches[matchInFocus].scrollIntoView({
        block: 'center',
      });
    }
  }, [matchInFocus, matches]);

  const moveToNextMatch = useCallback(() => {
    if (matches) {
      const nextMatchIndex = (matchInFocus + 1) % matches.length;
      setMatchInFocus(nextMatchIndex);
      highlightMatch(matches[nextMatchIndex]);
      unHighlightMatch(matches[matchInFocus]);
    }
  }, [matchInFocus, matches]);

  const moveToPreviousMatch = useCallback(() => {
    if (matches) {
      const previousMatchIndex =
        matchInFocus - 1 < 0 ? matches.length - 1 : matchInFocus - 1;
      setMatchInFocus(previousMatchIndex);
      highlightMatch(matches[previousMatchIndex]);
      unHighlightMatch(matches[matchInFocus]);
    }
  }, [matchInFocus, matches]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && search && matches && matches.length > 0) {
        if (e.shiftKey) {
          moveToPreviousMatch();
        } else {
          moveToNextMatch();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [matchInFocus, matches, search, moveToNextMatch, moveToPreviousMatch]);

  const highlightMatch = (match: HTMLElement) => {
    match.classList.add('border', 'p-1');
  };

  const unHighlightMatch = (match: HTMLElement) => {
    match.classList.remove('border', 'p-1');
  };

  return (
    <div className="sticky top-header py-8 bg-blank-black w-full z-10">
      <div className="relative w-full flex flex-col gap-8">
        <div className="relative w-full">
          <input
            className={`p-8 text-blank-yellow bg-blank-black border border-blank-yellow bg-no-repeat bg-[96%_50%] bg-[size:16px] bg-[url('/icons/magnifyingGlass.svg')] focus:outline-hidden placeholder-blank-yellow focus:bg-blank-yellow focus:placeholder-blank-black focus:text-blank-black w-full`}
            placeholder="Finn noe"
            type="search"
            id="handbook-search"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {search && (
          <div className="flex flex-row gap-[20px] justify-center items-center">
            {matchInFocus !== -1 && (
              <button onClick={moveToPreviousMatch}>
                <Arrow strokeWidth={1} className="rotate-180 cursor-pointer" />
              </button>
            )}

            <p className="whitespace-nowrap min-w-[80px] text-center">
              {matches?.length
                ? `${matchInFocus + 1}/${matches?.length}`
                : 'Ingen treff for søket'}
            </p>
            {matchInFocus !== -1 && (
              <button onClick={moveToNextMatch} className="cursor-pointer">
                <Arrow strokeWidth={1} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
